import { useCallback, useMemo, useState, type ComponentType } from "react";
import { Grid3x3, LayoutGrid, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  canRoute,
  indexPorts,
  nextRoute,
  readStoredView,
  routedSourceId,
  writeStoredView,
  type RouterPort,
  type RouterView,
} from "./router-model";
import { RenameProvider } from "./rename";
import { RouterMatrix } from "./RouterMatrix";
import { RouterSelect } from "./RouterSelect";
import { RouterButtons } from "./RouterButtons";
import type { RouterProps, RouterViewProps } from "./types";

const VIEW_OPTIONS: {
  view: RouterView;
  label: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  { view: "matrix", label: "Matrix", icon: Grid3x3 },
  { view: "select", label: "List", icon: List },
  { view: "buttons", label: "Buttons", icon: LayoutGrid },
];

const browserStorage = () =>
  typeof window === "undefined" ? undefined : window.localStorage;

/**
 * Generic N-targets × M-sources router (at most one source per target),
 * with matrix, select-list and button-panel views and inline renaming.
 */
export function Router({
  sources,
  targets,
  routes,
  routeState,
  routeFallbackLabel,
  onRoute,
  onRenameSource,
  onRenameTarget,
  allowClear = false,
  viewStorageKey,
  defaultView = "matrix",
  disabled,
  sourceNoun = "Source",
  targetNoun = "Target",
}: RouterProps) {
  const [view, setViewState] = useState<RouterView>(() =>
    readStoredView(browserStorage(), viewStorageKey, defaultView),
  );

  const setView = (next: RouterView) => {
    setViewState(next);
    writeStoredView(browserStorage(), viewStorageKey, next);
  };

  const sourcesById = useMemo(() => indexPorts(sources), [sources]);

  const canRouteTarget = useCallback(
    (target: RouterPort) =>
      canRoute({ target, disabled, hasHandler: !!onRoute }),
    [disabled, onRoute],
  );

  const clickSource = useCallback(
    (targetId: string, sourceId: string) => {
      const next = nextRoute(
        routedSourceId(routes, targetId),
        sourceId,
        allowClear,
      );
      if (next !== undefined) onRoute?.(targetId, next);
    },
    [routes, allowClear, onRoute],
  );

  const clearRoute = useCallback(
    (targetId: string) => {
      if (allowClear && routedSourceId(routes, targetId) !== null) {
        onRoute?.(targetId, null);
      }
    },
    [routes, allowClear, onRoute],
  );

  const viewProps: RouterViewProps = {
    sources,
    targets,
    routes,
    routeState,
    routeFallbackLabel,
    sourcesById,
    allowClear,
    sourceNoun,
    targetNoun,
    canRouteTarget,
    clickSource,
    clearRoute,
  };

  return (
    <RenameProvider
      onRenameSource={onRenameSource}
      onRenameTarget={onRenameTarget}
      disabled={disabled}
    >
      <div data-testid="router" data-view={view} className="space-y-2">
        <div className="flex justify-end">
          <div
            className="flex rounded-md border"
            role="radiogroup"
            aria-label="Router view"
          >
            {VIEW_OPTIONS.map(({ view: option, label, icon: Icon }, i) => (
              <Button
                key={option}
                variant="ghost"
                size="sm"
                role="radio"
                aria-checked={view === option}
                aria-label={label}
                title={label}
                data-testid={`router-view-${option}`}
                className={cn(
                  "h-7 px-2",
                  i > 0 && "rounded-l-none",
                  i < VIEW_OPTIONS.length - 1 && "rounded-r-none",
                  view === option && "bg-accent",
                )}
                onClick={() => setView(option)}
              >
                <Icon className="h-4 w-4" />
              </Button>
            ))}
          </div>
        </div>

        {view === "matrix" && <RouterMatrix {...viewProps} />}
        {view === "select" && <RouterSelect {...viewProps} />}
        {view === "buttons" && <RouterButtons {...viewProps} />}
      </div>
    </RenameProvider>
  );
}
